import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { TERMS_PATH } from '../legal/operator';

export default function ParentChildInvite() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auth/child-invites/');
      setInvites(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e.response?.status === 403) {
        setInvites([]);
        return;
      }
      setError(e.response?.data?.detail || 'Не удалось загрузить ссылки');
    }
  }, []);

  useEffect(() => {
    if (user?.is_parent) void load();
  }, [load, user?.is_parent]);

  if (!user?.is_parent) return null;

  async function handleCreate(e) {
    e.preventDefault();
    if (!consent) {
      setError('Отметьте согласие на обработку персональных данных ребёнка');
      return;
    }
    if (!terms) {
      setError('Примите пользовательское соглашение для аккаунта ребёнка');
      return;
    }
    setSaving(true);
    setError('');
    setHint('');
    try {
      const res = await api.post('/auth/child-invites/', {
        child_first_name: firstName.trim(),
        child_pdn_consent: true,
        terms_accepted: true,
      });
      setFirstName('');
      setConsent(false);
      setTerms(false);
      setHint(`Ссылка для ${res.data.child_first_name} готова. Отправьте её ребёнку.`);
      await load();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const messages = Object.entries(data)
          .map(([key, val]) => (Array.isArray(val) ? val.join(', ') : val))
          .join(' ');
        setError(messages || 'Не удалось создать ссылку');
      } else {
        setError('Не удалось создать ссылку');
      }
    } finally {
      setSaving(false);
    }
  }

  async function copyUrl(url) {
    setError('');
    try {
      await navigator.clipboard.writeText(url);
      setHint('Ссылка скопирована.');
    } catch {
      setHint(url);
    }
  }

  return (
    <section className="billing-section">
      <h2>Пригласить ребёнка</h2>
      <p className="text-secondary">
        Укажите имя, дайте согласие на его данные, примите пользовательское соглашение для его аккаунта и отправьте ссылку.
        Ребёнок сам создаст логин и пароль. Ссылка одноразовая и действует 30 дней.
        Так можно зарегистрировать и ребёнка младше 14 лет.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {hint && <div className="alert alert-success">{hint}</div>}
      <form onSubmit={handleCreate}>
        <div className="form-group">
          <label htmlFor="child_first_name">Имя ребёнка</label>
          <input
            id="child_first_name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            maxLength={100}
          />
        </div>
        <fieldset className="pdn-consent-block">
          <legend>Согласие на данные ребёнка</legend>
          <label className="pdn-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>
              Даю отдельное согласие на обработку персональных данных этого ребёнка
              (имя, логин, почта, учебные сведения).{' '}
              <Link to="/consent#child" target="_blank" rel="noopener noreferrer">
                Текст согласия
              </Link>
            </span>
          </label>
        </fieldset>
        <fieldset className="pdn-consent-block">
          <legend>Пользовательское соглашение</legend>
          <label className="pdn-consent-label">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
            />
            <span>
              Принимаю{' '}
              <Link to={TERMS_PATH} target="_blank" rel="noopener noreferrer">
                пользовательское соглашение
              </Link>
              {' '}для аккаунта этого ребёнка.
            </span>
          </label>
        </fieldset>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Создаём…' : 'Создать ссылку'}
        </button>
      </form>
      {invites.length > 0 && (
        <ul className="list-simple" style={{ marginTop: '1rem' }}>
          {invites.map((invite) => (
            <li key={invite.token}>
              <strong>{invite.child_first_name}</strong>
              <div className="invite-link-row" style={{ marginTop: '0.35rem' }}>
                <code className="invite-url">{invite.url}</code>
                <button
                  type="button"
                  className="btn btn-xs btn-outline"
                  onClick={() => copyUrl(invite.url)}
                >
                  Копировать
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
